/**
 * Composable for cleaning auth redirect parameters from the URL after Keycloak redirects.
 * --------------------------------------------------------------------------------------
 * This composable handles the automatic removal of authentication-related
 * query parameters after Keycloak redirects to prevent sensitive information
 * from being exposed in the browser history.
 */

/** Timeout for watching after Keycloak initialization (2 seconds) */
const REDIRECT_WATCH_TIMEOUT_MS = 2000;

/** Interval for checking redirect params during auth flow (50ms) */
const REDIRECT_CHECK_INTERVAL_MS = 50;

/** Authentication-related redirect parameter keys to be cleaned */
const AUTH_REDIRECT_PARAM_KEYS = ['state', 'session_state', 'code', 'error'] as const;

export function useAuthRedirectCleaner() {
  let redirectCheckInterval: ReturnType<typeof setInterval> | null = null;
  let cleanupTimeout: ReturnType<typeof setTimeout> | null = null;
  let isActive = false;
  const router = useRouter();
  const nuxtApp = useNuxtApp();

  /**
   * Checks if the current URL search params contain authentication-related redirect parameters
   */
  function hasAuthParams(): boolean {
    const params = new URLSearchParams(window.location.search);
    return AUTH_REDIRECT_PARAM_KEYS.some(key => params.has(key));
  }

  /**
   * Removes authentication redirect parameters from the URL via window.history.replaceState
   */
  function cleanUrl(): void {
    console.debug('[auth-redirect-cleaner]', 'Cleaning auth redirect params from URL');

    const params = new URLSearchParams(window.location.search);
    AUTH_REDIRECT_PARAM_KEYS.forEach(key => params.delete(key));

    const search = params.toString() ? `?${params.toString()}` : '';

    nuxtApp.hook('app:beforeMount', () => {
      try {
        router.replace(window.location.pathname + search + window.location.hash);
      } catch (error) {
        console.error('[auth-redirect-cleaner]', 'Error cleaning URL redirect params:', error);
      }
    });
    stopWatching();
  }

  /**
   * Stops the redirect watching interval and cleans up resources
   */
  function stopWatching(): void {
    if (redirectCheckInterval) {
      clearInterval(redirectCheckInterval);
      redirectCheckInterval = null;
    }
    if (cleanupTimeout) {
      clearTimeout(cleanupTimeout);
      cleanupTimeout = null;
    }

    isActive = false;
    console.debug('[auth-redirect-cleaner]', 'Watching stopped');
  }

  /**
   * Starts watching for redirect params and automatically cleans them
   */
  function startWatching(): void {
    if (isActive) {
      console.warn('[auth-redirect-cleaner]', 'Watching already active');
      return;
    }

    console.debug('[auth-redirect-cleaner]', 'Starting watching');
    isActive = true;

    // Start the interval for checking redirect params
    redirectCheckInterval = setInterval(cleanCurrentParams, REDIRECT_CHECK_INTERVAL_MS);

    // Set up automatic cleanup after timeout
    cleanupTimeout = setTimeout(() => {
      console.debug('[auth-redirect-cleaner]', 'Watching timeout reached, stopping');
      stopWatching();
    }, REDIRECT_WATCH_TIMEOUT_MS);
  }

  /**
   * Checks the current route query and cleans auth redirect parameters if present
   */
  function cleanCurrentParams(): void {
    try {
      if (hasAuthParams()) {
        cleanUrl();
      }
    } catch (error) {
      console.error('[auth-redirect-cleaner]', 'Error cleaning URL redirect params:', error);
    }
  }

  /**
   * Check if redirect watching is currently active
   */
  function isWatchingActive(): boolean {
    return isActive;
  }

  return {
    startWatching,
    stopWatching,
    cleanCurrentParams,
    isWatchingActive,
  };
}
